import { Capacitor } from "@capacitor/core";

/**
 * Options for sharing content.
 * Matches Capacitor's ShareOptions interface.
 */
export interface ShareOptions {
  /**
   * Title for the share dialog
   */
  title?: string;
  /**
   * Text content to share
   */
  text?: string;
  /**
   * URL to share
   */
  url?: string;
  /**
   * Optional array of file URIs to share (native only)
   */
  files?: string[];
  /**
   * Dialog title (Android only)
   */
  dialogTitle?: string;
}

/**
 * Result from a share operation.
 */
export interface ShareResult {
  /**
   * The activity type that was chosen (iOS only)
   */
  activityType?: string;
}

/**
 * Shares content using the native share dialog or web share API.
 * On native platforms, opens the system share sheet.
 * On web, uses the Web Share API if available, otherwise falls back to mailto.
 *
 * @param options - The content to share
 * @returns Share result with activity type (iOS only)
 */
export async function share(options: ShareOptions): Promise<ShareResult | undefined> {
  const { title, text, url, files, dialogTitle } = options;

  if (Capacitor.isNativePlatform()) {
    const { Share } = await import("@capacitor/share");
    return await Share.share({
      title,
      text,
      url,
      files,
      dialogTitle,
    });
  }

  // Web fallback: use Web Share API if available
  if (typeof navigator !== "undefined" && navigator.share) {
    await navigator.share({
      title,
      text,
      url,
    });
    return undefined;
  }

  // Final fallback: open mailto link
  const mailtoSubject = encodeURIComponent(title ?? "");
  const mailtoBody = encodeURIComponent(`${text ?? ""}\n${url ?? ""}`);
  window.open(
    `mailto:?subject=${mailtoSubject}&body=${mailtoBody}`,
    "_blank",
    "noopener,noreferrer"
  );
  return undefined;
}
